/**
 * Stakeholder Role Enum
 * Represents the influence or decision-making role a contact plays within their organization.
 * These roles are universal across all clients and help categorize contacts by their involvement level.
 */
export enum StakeholderRole {
  // Decision-Making Roles
  EXECUTIVE_SPONSOR = "EXECUTIVE_SPONSOR",
  ECONOMIC_BUYER = "ECONOMIC_BUYER",
  TECHNICAL_BUYER = "TECHNICAL_BUYER",
  BUSINESS_BUYER = "BUSINESS_BUYER",

  // Influence & Usage Roles
  CHAMPION_ADVOCATE = "CHAMPION_ADVOCATE",
  INFLUENCER = "INFLUENCER",
  END_USER = "END_USER",
  POWER_USER = "POWER_USER",
  ADMINISTRATOR = "ADMINISTRATOR",

  // Specialized Roles
  TECHNICAL_CONTACT = "TECHNICAL_CONTACT",
  BILLING_CONTACT = "BILLING_CONTACT",
  LEGAL_PROCUREMENT_CONTACT = "LEGAL_PROCUREMENT_CONTACT",
  PROJECT_MANAGER = "PROJECT_MANAGER",

  // Evaluation Roles
  EVALUATOR = "EVALUATOR",
  GATEKEEPER = "GATEKEEPER",
  BLOCKER_SKEPTIC = "BLOCKER_SKEPTIC",
}

/**
 * Category groupings for stakeholder roles
 */
export enum StakeholderRoleCategory {
  DECISION_MAKING = "DECISION_MAKING",
  INFLUENCE_USAGE = "INFLUENCE_USAGE",
  SPECIALIZED = "SPECIALIZED",
  EVALUATION = "EVALUATION",
}

/**
 * Metadata structure for each stakeholder role
 */
export interface StakeholderRoleMetadata {
  label: string;
  description: string;
  category: StakeholderRoleCategory;
  categoryLabel: string;
  sortOrder: number;
}

/**
 * Complete metadata for all stakeholder roles
 * Used for UI rendering (dropdowns, tooltips, labels)
 */
export const STAKEHOLDER_ROLE_METADATA: Record<
  StakeholderRole,
  StakeholderRoleMetadata
> = {
  // Decision-Making Roles
  [StakeholderRole.EXECUTIVE_SPONSOR]: {
    label: "Executive Sponsor",
    description: "C-level or VP who champions and funds the initiative",
    category: StakeholderRoleCategory.DECISION_MAKING,
    categoryLabel: "Decision-Making Roles",
    sortOrder: 1,
  },
  [StakeholderRole.ECONOMIC_BUYER]: {
    label: "Economic Buyer",
    description: "Has budget authority and makes final purchasing decisions",
    category: StakeholderRoleCategory.DECISION_MAKING,
    categoryLabel: "Decision-Making Roles",
    sortOrder: 2,
  },
  [StakeholderRole.TECHNICAL_BUYER]: {
    label: "Technical Buyer",
    description: "Evaluates technical requirements and compatibility",
    category: StakeholderRoleCategory.DECISION_MAKING,
    categoryLabel: "Decision-Making Roles",
    sortOrder: 3,
  },
  [StakeholderRole.BUSINESS_BUYER]: {
    label: "Business Buyer",
    description: "Evaluates business value and ROI",
    category: StakeholderRoleCategory.DECISION_MAKING,
    categoryLabel: "Decision-Making Roles",
    sortOrder: 4,
  },

  // Influence & Usage Roles
  [StakeholderRole.CHAMPION_ADVOCATE]: {
    label: "Champion/Advocate",
    description: "Internal promoter who drives adoption",
    category: StakeholderRoleCategory.INFLUENCE_USAGE,
    categoryLabel: "Influence & Usage Roles",
    sortOrder: 5,
  },
  [StakeholderRole.INFLUENCER]: {
    label: "Influencer",
    description: "Provides input but doesn't make final decisions",
    category: StakeholderRoleCategory.INFLUENCE_USAGE,
    categoryLabel: "Influence & Usage Roles",
    sortOrder: 6,
  },
  [StakeholderRole.END_USER]: {
    label: "End User",
    description: "Day-to-day users of the product",
    category: StakeholderRoleCategory.INFLUENCE_USAGE,
    categoryLabel: "Influence & Usage Roles",
    sortOrder: 7,
  },
  [StakeholderRole.POWER_USER]: {
    label: "Power User",
    description: "Advanced users who often train others",
    category: StakeholderRoleCategory.INFLUENCE_USAGE,
    categoryLabel: "Influence & Usage Roles",
    sortOrder: 8,
  },
  [StakeholderRole.ADMINISTRATOR]: {
    label: "Administrator",
    description: "Manages the product configuration and users",
    category: StakeholderRoleCategory.INFLUENCE_USAGE,
    categoryLabel: "Influence & Usage Roles",
    sortOrder: 9,
  },

  // Specialized Roles
  [StakeholderRole.TECHNICAL_CONTACT]: {
    label: "Technical Contact",
    description: "IT point of contact for integrations/support",
    category: StakeholderRoleCategory.SPECIALIZED,
    categoryLabel: "Specialized Roles",
    sortOrder: 10,
  },
  [StakeholderRole.BILLING_CONTACT]: {
    label: "Billing Contact",
    description: "Handles invoicing and payment",
    category: StakeholderRoleCategory.SPECIALIZED,
    categoryLabel: "Specialized Roles",
    sortOrder: 11,
  },
  [StakeholderRole.LEGAL_PROCUREMENT_CONTACT]: {
    label: "Legal/Procurement Contact",
    description: "Manages contracts and compliance",
    category: StakeholderRoleCategory.SPECIALIZED,
    categoryLabel: "Specialized Roles",
    sortOrder: 12,
  },
  [StakeholderRole.PROJECT_MANAGER]: {
    label: "Project Manager",
    description: "Coordinates implementation",
    category: StakeholderRoleCategory.SPECIALIZED,
    categoryLabel: "Specialized Roles",
    sortOrder: 13,
  },

  // Evaluation Roles
  [StakeholderRole.EVALUATOR]: {
    label: "Evaluator",
    description: "Participates in proof of concept or trials",
    category: StakeholderRoleCategory.EVALUATION,
    categoryLabel: "Evaluation Roles",
    sortOrder: 14,
  },
  [StakeholderRole.GATEKEEPER]: {
    label: "Gatekeeper",
    description: "Controls access to decision makers",
    category: StakeholderRoleCategory.EVALUATION,
    categoryLabel: "Evaluation Roles",
    sortOrder: 15,
  },
  [StakeholderRole.BLOCKER_SKEPTIC]: {
    label: "Blocker/Skeptic",
    description: "Has concerns or opposes the solution",
    category: StakeholderRoleCategory.EVALUATION,
    categoryLabel: "Evaluation Roles",
    sortOrder: 16,
  },
};

/**
 * Helper function to get stakeholder roles grouped by category
 * Useful for rendering categorized dropdowns
 */
export const getStakeholderRolesByCategory = (): Map<
  StakeholderRoleCategory,
  Array<{ role: StakeholderRole; metadata: StakeholderRoleMetadata }>
> => {
  const grouped = new Map<
    StakeholderRoleCategory,
    Array<{ role: StakeholderRole; metadata: StakeholderRoleMetadata }>
  >();

  // Initialize all categories
  Object.values(StakeholderRoleCategory).forEach((category) => {
    grouped.set(category, []);
  });

  // Group roles by category
  Object.entries(STAKEHOLDER_ROLE_METADATA).forEach(([roleKey, metadata]) => {
    const role = roleKey as StakeholderRole;
    grouped.get(metadata.category)!.push({ role, metadata });
  });

  // Sort each category by sortOrder
  grouped.forEach((roles) => {
    roles.sort((a, b) => a.metadata.sortOrder - b.metadata.sortOrder);
  });

  return grouped;
};

/**
 * Get display label for a stakeholder role
 */
export const getStakeholderRoleLabel = (role: StakeholderRole): string => {
  return STAKEHOLDER_ROLE_METADATA[role]?.label || role;
};

/**
 * Get description for a stakeholder role
 */
export const getStakeholderRoleDescription = (
  role: StakeholderRole
): string => {
  return STAKEHOLDER_ROLE_METADATA[role]?.description || "";
};

/**
 * Get all stakeholder roles sorted by category and sort order
 */
export const getAllStakeholderRolesSorted = (): Array<{
  role: StakeholderRole;
  metadata: StakeholderRoleMetadata;
}> => {
  return Object.entries(STAKEHOLDER_ROLE_METADATA)
    .map(([role, metadata]) => ({
      role: role as StakeholderRole,
      metadata,
    }))
    .sort((a, b) => a.metadata.sortOrder - b.metadata.sortOrder);
};

/**
 * Maximum number of stakeholder roles allowed per contact
 */
export const MAX_STAKEHOLDER_ROLES = 3;
